import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

// Create context
const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => useContext(CartContext);

// Cart Provider
export const CartProvider = ({ children }) => {
  // Helper to get current user id from localStorage
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.id || null;
    } catch {
      return null;
    }
  };

  // Helper to get user-specific localStorage key
  const getCartKey = (userId) => (userId ? `cart_${userId}` : "cart_guest");

  // Load cart from user-specific localStorage or default to empty array
  const [cart, setCart] = useState(() => {
    const key = getCartKey(getUserId());
    const storedCart = localStorage.getItem(key);
    return storedCart ? JSON.parse(storedCart) : [];
  });

  // Safe number parsing helper
  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Lightweight function to fetch essential product details for cart validation
  const fetchProductDetails = async (productId) => {
    try {
      console.log(`Fetching essential details for cart product ${productId}`);

      const res = await fetch(`${BASE_URL}/products/${productId}`);
      console.log("Product API raw response:", res);

      // Handle different response statuses
      if (!res.ok) {
        if (res.status === 404) {
          console.error("Product not found - 404");
          return { error: "Product not found", notFound: true };
        } else if (res.status === 204) {
          console.error("Product not found - 204");
          return { error: "Product not found", notFound: true };
        } else {
          console.error("Product API error:", res.status, res.statusText);
          return { error: `Failed to fetch product: ${res.status} ${res.statusText}` };
        }
      }

      const data = await res.json();
      console.log("Product API response:", data);

      // Check for API response indicating product not found
      if (data?.status === "204" && data?.message === "Product not found") {
        console.error("Product not found - API returned 204 status");
        return { error: "Product not found", notFound: true };
      }

      const p = data?.data && !Array.isArray(data.data)
        ? data.data
        : Array.isArray(data?.data)
        ? data.data[0]
        : data?.product;

      if (!p) {
        console.error("Product data is null or undefined");
        return { error: "Product not found", notFound: true };
      }

      console.log("Product data found:", p);

      // Parse main image
      let images = [];
      try {
        images = JSON.parse(p.product_images || "[]");
      } catch {}

      const mainImage = images.length > 0 ? `${CDN_URL}/${images[0]}` : "/placeholder.png";

      // Return only essential product details needed for cart
      const essentialDetails = {
        id: String(p.id),
        name: p.product_name ?? "",
        // Fix price mappings: max_retail_price is original price, your_price is offer price
        price: safeParseNumber(p.max_retail_price, 0), // Original price
        offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
        // Also include legacy fields for backward compatibility
        max_retail_price: safeParseNumber(p.max_retail_price, 0),
        your_price: safeParseNumber(p.your_price, 0),
        categoryId: String(p.category_id || ""),
        categoryName: p.category_name ?? "",
        subcategoryId: String(p.subcategory_id || ""),
        subcategoryName: p.subcategory_name ?? "",
        image: mainImage,
        // Add minimum order quantity field
        min_order_qty: safeParseNumber(p.min_order_qty, 1),
        minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
        // Keep any additional fields that might be needed for compatibility
        description: p.description ?? "",
        brand: p.brand_name,
        model: p.model_name,
        material: p.material,
        color: p.color,
        colorCode: p.color_code,
        size: p.size,
        quantity: parseInt(p.quantity) || 0,
        sku: p.sku,
        averageRating: p.average_rating,
        returnPolicy: p.product_policy,
        status: p.status,
      };

      console.log("Essential product details extracted:", essentialDetails);
      return { product: essentialDetails };

    } catch (error) {
      console.error(`Error fetching product details for ${productId}:`, error.message);
      return { error: "Network error or API unavailable" };
    }
  };

  // Validate a single cart item using lightweight API call
  const validateCartItem = async (cartItem) => {
    try {
      console.log(
        `Validating cart product ${cartItem.id} (${cartItem.name})`,
      );

      const result = await fetchProductDetails(cartItem.id);

      if (result.error) {
        console.log(
          `❌ Cart product ${cartItem.id} - ${result.error}`,
        );

        return {
          isValid: false,
          cartItem,
          reason: result.notFound ? "Product no longer exists" : result.error,
        };
      }

      if (result.product) {
        // Preserve cart-specific data (quantity, customization) while updating product data
        const updatedCartItem = {
          ...result.product,
          quantity: cartItem.quantity,
          customization: cartItem.customization || {},
          // Preserve any cart-specific fields
          originalCategoryId: cartItem.originalCategoryId,
        };

        console.log(
          `✅ Cart product ${cartItem.id} is VALID - updated with fresh data`,
          updatedCartItem
        );

        return {
          isValid: true,
          cartItem: updatedCartItem,
          originalCartItem: cartItem
        };
      } else {
        console.log(
          `❌ Cart product ${cartItem.id} - no product data returned`,
        );
        return {
          isValid: false,
          cartItem,
          reason: "Product data not available",
        };
      }
    } catch (error) {
      console.error(
        `❌ Error validating cart product ${cartItem.id}:`,
        error.message,
      );

      // For any errors, assume product exists but temporarily unavailable
      // Return original cart item data
      console.log(
        `Cart product ${cartItem.id} - validation error, keeping original data`,
      );
      return { isValid: true, cartItem };
    }
  };

  // Validate all products in user's cart and update with fresh data
  const validateUserCart = async () => {
    if (cart.length === 0) {
      return { validCartItems: [], invalidCartItems: [] };
    }

    console.log("Starting cart validation and data refresh");
    console.log(
      "Cart items to validate:",
      cart.map((item) => ({ id: item.id, name: item.name, quantity: item.quantity })),
    );

    const validationPromises = cart.map((cartItem) =>
      validateCartItem(cartItem),
    );

    try {
      const results = await Promise.all(validationPromises);

      const validCartItems = results
        .filter((result) => result.isValid)
        .map((result) => result.cartItem);

      const invalidCartItems = results
        .filter((result) => !result.isValid)
        .map((result) => ({ ...result.cartItem, reason: result.reason }));

      console.log("=== CART VALIDATION & UPDATE SUMMARY ===");
      console.log(`Total cart items checked: ${cart.length}`);
      console.log(`Valid items (keeping with updated data): ${validCartItems.length}`);
      console.log(`Invalid items (removing): ${invalidCartItems.length}`);

      if (invalidCartItems.length > 0) {
        console.log(
          "Cart items to remove:",
          invalidCartItems.map((item) => ({
            id: item.id,
            name: item.name,
            reason: item.reason,
          })),
        );
      }

      if (validCartItems.length > 0) {
        console.log(
          "Cart items updated with fresh data:",
          validCartItems.map((item) => ({
            id: item.id,
            name: item.name,
            categoryName: item.categoryName,
            price: item.price,
            offerPrice: item.offerPrice,
            minimumOrderQty: item.minimumOrderQty,
            quantity: item.quantity
          })),
        );
      }

      return { validCartItems, invalidCartItems };
    } catch (error) {
      console.error("Error during cart validation:", error);
      return { validCartItems: cart, invalidCartItems: [] };
    }
  };

  // Update cart with fresh product data (for valid items)
  const updateCartWithFreshData = (validCartItems) => {
    if (validCartItems.length === 0) return;

    setCart(validCartItems);
  };

  // Remove invalid products from cart (silent operation - toasts handled by component)
  const removeInvalidCartItems = (invalidCartItems) => {
    if (invalidCartItems.length === 0) return;

    const invalidProductIds = invalidCartItems.map((item) => item.id);
    setCart((prev) => prev.filter(
      (item) => !invalidProductIds.includes(item.id)
    ));

    // No toast notifications here - handled by calling component to prevent duplicates
  };

  // Validate and clean user cart with fresh data (main function to call)
  const validateAndCleanCart = async () => {
    try {
      const { validCartItems, invalidCartItems } = await validateUserCart();

      // Update cart with fresh data for valid items
      if (validCartItems.length > 0) {
        updateCartWithFreshData(validCartItems);
      }

      // Remove invalid items
      if (invalidCartItems.length > 0) {
        removeInvalidCartItems(invalidCartItems);
      }

      return { validCartItems, invalidCartItems };
    } catch (error) {
      console.error("Error validating and cleaning cart:", error);
      return { validCartItems: cart, invalidCartItems: [] };
    }
  };

  // Persist cart to user-specific localStorage whenever it changes
  useEffect(() => {
    const key = getCartKey(getUserId());
    localStorage.setItem(key, JSON.stringify(cart));
  }, [cart]);

  // Listen for login/logout to switch carts instantly
  useEffect(() => {
    const handleAuthChange = () => {
      const key = getCartKey(getUserId());
      const storedCart = localStorage.getItem(key);
      setCart(storedCart ? JSON.parse(storedCart) : []);
    };

    window.addEventListener("auth-change", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);

    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  // Add product to cart
  const addToCart = (product, quantity = 1, customization = {}) => {
    setCart((prev) => {
      // Check if the product with the same customization already exists
      const existingIndex = prev.findIndex(
        (item) =>
          item.id === product.id &&
          JSON.stringify(item.customization) === JSON.stringify(customization),
      );

      if (existingIndex > -1) {
        const updatedCart = [...prev];
        updatedCart[existingIndex].quantity += quantity;
        // Ensure category name is updated if it was missing
        if (!updatedCart[existingIndex].categoryName && product.categoryName) {
          updatedCart[existingIndex].categoryName = product.categoryName;
        }
        // Also update if categoryId is a name but categoryName is missing
        if (
          !updatedCart[existingIndex].categoryName &&
          typeof product.categoryId === "string" &&
          isNaN(product.categoryId)
        ) {
          updatedCart[existingIndex].categoryName = product.categoryId;
        }
        return updatedCart;
      }

      // Add new item - ensure all product fields are preserved
      const newItem = {
        ...product,
        quantity,
        customization,
        // Ensure category name is preserved - prioritize categoryName over categoryId
        categoryName:
          product.categoryName ||
          (typeof product.categoryId === "string" && isNaN(product.categoryId)
            ? product.categoryId
            : "Uncategorized"),
        // Keep original categoryId for reference
        originalCategoryId: product.categoryId,
      };
      return [...prev, newItem];
    });
  };

  // Remove item by index
  const removeFromCart = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  // Update quantity of an item by index with minimum order quantity validation
  const updateQuantity = (index, quantity) => {
    setCart((prev) => {
      const updatedCart = [...prev];
      const item = updatedCart[index];

      // Get minimum order quantity
      const minimumOrderQty = item?.minimumOrderQty || item?.min_order_qty || 1;

      // Ensure quantity doesn't go below minimum order quantity
      const validQuantity = Math.max(quantity, minimumOrderQty);

      updatedCart[index].quantity = validQuantity;
      return updatedCart;
    });
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
  };

  // Remove specific product by ID
  const removeProductById = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        removeProductById,
        // New validation functions
        validateCartItem,
        validateUserCart,
        updateCartWithFreshData,
        removeInvalidCartItems,
        validateAndCleanCart,
        fetchProductDetails,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
